import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { translate } from 'react-i18next';
import { observer, inject } from 'mobx-react';
import b from 'b_';

import FuseTextfield from '../FuseControls/FuseTextfield';
import FuseButton from '../FuseControls/FuseButton';
import MobileModal from '../MobileModal/MobileModal';
import Icon from '../Icon/Icon';
import { ResearchStoreProp, EnvStoreProps } from '../../stores/props';
import { userAgents } from '../../userAgents';
import TopBarDropdown from '../TopBarDropdown/TopBarDropdown';
import { isFuse5Enabled } from '../../fuseFeatureFlag';
import OldUserAgentMenu from './OldUserAgentMenu';
import TextStore from '../../stores/helpers/TextStore';
import { FTBody, FTCaption } from '../Fuse/Text';
import './UserAgentMenu.css';

const block = b.lock('NewUserAgentMenu');

class NewUserAgentMenu extends Component {
    static propTypes = {
        t: PropTypes.func.isRequired,
        research: ResearchStoreProp,
        env: EnvStoreProps,
        afterChange: PropTypes.func,
    };

    static defaultProps = {
        afterChange: () => {},
    };

    constructor(props) {
        super(props);

        this.state = {
            activeDropdownKeys: [],
        };

        this.textAreaStore = new TextStore(props.research.userAgent || '');
    }

    componentDidUpdate(prevProps) {
        // Update TextStore when userAgent prop changes
        if (prevProps.research.userAgent !== this.props.research.userAgent) {
            this.textAreaStore.setValue(this.props.research.userAgent || '');
        }
    }



    onDropdownClick = (key) => {
        this.setState((prevState) => {
            const { activeDropdownKeys } = prevState;
            const isCurrentlyActive = activeDropdownKeys.includes(key);

            const newKeys = isCurrentlyActive
                ? activeDropdownKeys.filter((k) => k !== key)
                : [...activeDropdownKeys, key];

            // Update expansion state

            if (isCurrentlyActive) {
                return { activeDropdownKeys: newKeys };
            } else {
                return { activeDropdownKeys: newKeys };
            }
        });
    };

    setUserAgent = (newValue) => {
        this.props.research.setUserAgent(newValue);
        this.props.research.userAgentMenu.close();
        this.props.afterChange();
    };

    applyCustomUserAgent = () => {
        const customValue = this.textAreaStore.getValue();
        if (customValue.trim()) {
            this.setUserAgent(customValue);
        }
    };

    resetUserAgent = () => {
        this.props.research.setUserAgent('');
        this.textAreaStore.setValue('');
        this.props.research.userAgentMenu.close();
        this.props.afterChange();
    };

    cancelModal = () => {
        this.props.research.userAgentMenu.close();
    };

    renderContent() {
        const { t, research } = this.props;
        const { activeDropdownKeys } = this.state;
        const currentUserAgent = research.userAgent;

        return <Fragment>
            {/* Section header */}
            <div className={block('SectionHeader')}>
                {t('userAgentMenu.popupTitleAction')}
            </div>

            {/* Divider before custom section */}
            <div className={block('Divider')} />

            {Object.keys((userAgents)).map((os, index, array) => <Fragment key={os}>
                <div className={block('OS')}>
                    <FTBody
                        className={block('OSCaption')}
                        onClick={() => this.onDropdownClick(os)}
                    >
                        <Icon
                            type={`os_${os.toLowerCase()}`}
                            style={{ fontSize: 24 }}
                        />
                        <span className={block('OSCaptionText')}>
                            {t('userAgentMenu.' + os)}
                        </span>
                        <Icon
                            className={activeDropdownKeys.includes(os) ? 'NewUserAgentMenu__DropdownArrow--expanded' :
                                ''}
                            type="fuse_down_arrow"
                        />
                    </FTBody>
                    {activeDropdownKeys.includes(os) ? Object.entries(userAgents[os]).map(([browser, ua]) => {
                        const isSelected = currentUserAgent === ua;
                        return <FTCaption
                            key={browser}
                            className={block('Browser', { selected: isSelected })}
                            onClick={() => this.setUserAgent(ua)}
                        >
                            <Icon
                                type={`browser_${browser.toLowerCase()}`}
                                style={{ fontSize: 24 }}
                            />
                            <span>{t('userAgentMenu.' + browser)}</span>
                        </FTCaption>;
                    }) : null}
                </div>

                {/* Add divider between OS sections, but not after the last one */}
                {index < array.length - 1 && <div className={block('Divider')} />}
            </Fragment>)}

            {/* Divider before custom section */}
            <div className={block('Divider')} />

            {/* Custom section header */}
            <div className={block('SectionHeader')}>
                {t('userAgentMenu.custom')}
            </div>

            {/* Custom input section */}
            <FuseTextfield
                store={this.textAreaStore}
                className={block('CustomInput')}
                placeholder={t('userAgentMenu.enterString')}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        this.applyCustomUserAgent();
                    }
                }}
            />
        </Fragment>;
    }

    renderFooterActions() {
        const { t, research } = this.props;
        const hasUserAgent = Boolean(research.userAgent && research.userAgent.trim());

        return <Fragment>
            <FuseButton
                type={FuseButton.type.TERTIARY}
                color={FuseButton.color.BLUE}
                onClick={this.cancelModal}
            >
                {t('userAgentMenu.cancel')}
            </FuseButton>
            <FuseButton
                type={FuseButton.type.PRIMARY}
                color={FuseButton.color.RED}
                onClick={this.resetUserAgent}
                disabled={!hasUserAgent}
            >
                {t('userAgentMenu.reset')}
            </FuseButton>
        </Fragment>;
    }

    render() {
        const { research, env } = this.props;

        return (env.screen.isMobile || env.screen.isTablet || env.isTouchDevice)
            ? <MobileModal
                isOpen={research.userAgentMenu.isOpen}
                onClose={research.userAgentMenu.close}
                shouldCloseOnOverlayClick={false}
                footerActions={this.renderFooterActions()}
            >{this.renderContent()}</MobileModal>
            : <TopBarDropdown
                className={block()}
                type="research"
                onClose={research.userAgentMenu.close}
            >{this.renderContent()}</TopBarDropdown>;
    }
}

const WrappedNewUserAgentMenu = translate()(inject('research', 'env')(observer(NewUserAgentMenu)));

const UserAgentMenu = (props) => {
    return isFuse5Enabled() ? <WrappedNewUserAgentMenu {...props} /> : <OldUserAgentMenu {...props} />;
};

export default UserAgentMenu;
